import type { Translate } from "@/lib/i18n/translate";
import {
  AlignLeft,
  CircleDot,
  FileText,
  Hash,
  Heading,
  List,
  Mail,
  Minus,
  Phone,
  Search,
  SquareCheckBig,
  Type,
  User,
} from "lucide-react";
/**
 * Field palette for the Formulare module builder — the left rail of draggable
 * field types grouped into "Basis", "Auswahl" and "Kontakt & Zeit". 1:1 with
 * the prototype (`isFormulare` content, source lines 1464–1485).
 */
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";

type PaletteItem = {
  readonly icon: ReactNode;
  readonly label: string;
  readonly iconBg: string;
  readonly iconColor: string;
};

const ICON = { width: 14, height: 14, strokeWidth: 1.9 } as const;

/** "Basis" group — neutral grey icons. */
function basis(t: Translate): readonly PaletteItem[] {
  return [
    {
      icon: <Type {...ICON} />,
      label: t("kurzerText"),
      iconBg: "#EEEFF2",
      iconColor: "#5B6472",
    },
    {
      icon: <AlignLeft {...ICON} />,
      label: t("langerText"),
      iconBg: "#EEEFF2",
      iconColor: "#5B6472",
    },
    {
      icon: <FileText {...ICON} />,
      label: t("textabschnitt"),
      iconBg: "#EEEFF2",
      iconColor: "#5B6472",
    },
    {
      icon: <Heading {...ICON} />,
      label: t("ueberschrift"),
      iconBg: "#EEEFF2",
      iconColor: "#5B6472",
    },
    {
      icon: <Minus {...ICON} />,
      label: t("trenner"),
      iconBg: "#EEEFF2",
      iconColor: "#5B6472",
    },
  ];
}

/** "Auswahl" group — purple icons. */
function auswahl(t: Translate): readonly PaletteItem[] {
  return [
    {
      icon: <List {...ICON} />,
      label: t("dropdown"),
      iconBg: "#EFEAFB",
      iconColor: "#7C5CD4",
    },
    {
      icon: <CircleDot {...ICON} />,
      label: t("einfachauswahl"),
      iconBg: "#EFEAFB",
      iconColor: "#7C5CD4",
    },
    {
      icon: <SquareCheckBig {...ICON} />,
      label: t("mehrfachauswahl"),
      iconBg: "#EFEAFB",
      iconColor: "#7C5CD4",
    },
    {
      icon: <Hash {...ICON} />,
      label: t("zahl"),
      iconBg: "#EFEAFB",
      iconColor: "#7C5CD4",
    },
  ];
}

/** "Kontakt & Zeit" group — blue icons. */
function kontakt(t: Translate): readonly PaletteItem[] {
  return [
    {
      icon: <User {...ICON} />,
      label: t("name"),
      iconBg: "#E7EEFB",
      iconColor: "#3E6FD0",
    },
    {
      icon: <Mail {...ICON} />,
      label: t("eMail"),
      iconBg: "#E7EEFB",
      iconColor: "#3E6FD0",
    },
    {
      icon: <Phone {...ICON} />,
      label: t("telefon"),
      iconBg: "#E7EEFB",
      iconColor: "#3E6FD0",
    },
  ];
}

function GroupLabel(props: {
  readonly children: ReactNode;
  readonly padding: string;
}): ReactNode {
  return (
    <div
      style={{
        fontFamily: H.label,
        fontSize: 9,
        letterSpacing: "0.13em",
        color: "rgba(11,15,25,0.32)",
        textTransform: "uppercase",
        padding: props.padding,
      }}
    >
      {props.children}
    </div>
  );
}

function PaletteRow(props: { readonly item: PaletteItem }): ReactNode {
  const { item } = props;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        background: "#fff",
        border: "1px solid #EDEDE7",
        borderRadius: 9,
        padding: "6px 9px",
        marginBottom: 6,
        fontSize: 12.5,
        color: H.ink,
        cursor: "grab",
      }}
    >
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: 7,
          background: item.iconBg,
          color: item.iconColor,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {item.icon}
      </span>
      {item.label}
    </div>
  );
}

/** The full left-hand field palette (search + three field-type groups). */
export function ModuleFormularePalette(): ReactNode {
  const t = useTranslations("marketing.moduleFormularePalette");
  return (
    <div
      style={{
        width: 206,
        flexShrink: 0,
        background: "#fff",
        borderRight: "1px solid #EAEAE5",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "11px 12px 4px", flexShrink: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#F4F4F1",
            border: "1px solid #EAEAE5",
            borderRadius: 9,
            padding: "8px 11px",
            color: "rgba(11,15,25,0.4)",
            fontSize: 12.5,
          }}
        >
          <Search width={14} height={14} strokeWidth={1.9} />
          {t("suchen")}
        </div>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          padding: "4px 12px 0",
        }}
      >
        <GroupLabel padding="9px 2px 7px">{t("basis")}</GroupLabel>
        {basis(t).map((item) => (
          <PaletteRow key={item.label} item={item} />
        ))}
        <GroupLabel padding="13px 2px 7px">{t("auswahl")}</GroupLabel>
        {auswahl(t).map((item) => (
          <PaletteRow key={item.label} item={item} />
        ))}
        <GroupLabel padding="13px 2px 7px">{t("kontaktAmpZeit")}</GroupLabel>
        {kontakt(t).map((item) => (
          <PaletteRow key={item.label} item={item} />
        ))}
      </div>
    </div>
  );
}
